import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import RecurringTaskForm from "~/components/tasks/CreateRecurringTaskModal/RecurringTaskForm";
import { loadTask } from "~/store/actions/actions.tasks";
import { makeCompaniesByProjectMembersSelector } from "~/store/selectors/selector.companies";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions";
import { makeTaskLabelsByProjectSelector } from "~/store/selectors/selector.labels";
import { getProjectById } from "~/store/selectors/selector.projects";
import { getSortedSubscribersByProjectId, getTaskSubscriberIds } from "~/store/selectors/selector.subscription";
import { getSubtasksNameAndAssigneeIdByTaskId } from "~/store/selectors/selector.subtasks";
import { makeTaskListsByProjectSelector } from "~/store/selectors/selector.taskLists";
import { getTaskById } from "~/store/selectors/selector.tasks";
import { makeTeamsByProjectMembersSelector } from "~/store/selectors/selector.teams";
import {
  canSeeTimeEstimates,
  getLoggedUserId,
  getUsersFromProjectMembersForTaskUserSelector,
  isLoggedUserClientSelector,
} from "~/store/selectors/selector.users";

const RecurringTaskFromTask = ({ open, taskId, projectId, onClose }) => {
  const dispatch = useDispatch();
  const [isFetched, setIsFetched] = useState(false);
  const task = useSelector((state) => getTaskById(state, taskId));

  useEffect(() => {
    dispatch(loadTask(projectId, taskId)).then(() => setIsFetched(true));
  }, [dispatch, projectId, taskId]);

  const users = useSelector((state) => getUsersFromProjectMembersForTaskUserSelector(state, projectId));
  const getTeamsByProjectMembersSelector = useMemo(makeTeamsByProjectMembersSelector, []);
  const getTaskListsByProjectSelector = useMemo(makeTaskListsByProjectSelector, []);
  const getCompaniesByProjectMembersSelector = useMemo(
    () => makeCompaniesByProjectMembersSelector(projectId),
    [projectId]
  );
  const getTaskLabelsByProjectSelector = useMemo(makeTaskLabelsByProjectSelector, []);
  const teams = useSelector((state) => getTeamsByProjectMembersSelector(state, projectId));
  const companies = useSelector(getCompaniesByProjectMembersSelector);
  const labels = useSelector((state) => getTaskLabelsByProjectSelector(state, projectId, ""));
  const taskLists = useSelector((state) => getTaskListsByProjectSelector(state, projectId));
  const canSeeEstimates = useSelector((state) => canSeeTimeEstimates(state, projectId));
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));
  const subscriberIds = useSelector((state) => {
    return getTaskSubscriberIds(state, taskId);
  });
  const loggedUserId = useSelector(getLoggedUserId);
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const subtasks = useSelector((state) => getSubtasksNameAndAssigneeIdByTaskId(state, taskId));
  const project = useSelector((state) => getProjectById(state, projectId).toJS());

  const projectMembers = useSelector((state) => getSortedSubscribersByProjectId(state, projectId));

  return isFetched ? (
    <RecurringTaskForm
      open={open}
      projectId={projectId}
      projectMembers={projectMembers}
      users={users}
      teams={teams}
      companies={companies}
      labels={labels}
      attachments={task?.attachments}
      taskLists={taskLists}
      canSeeTimeEstimates={canSeeEstimates}
      name={task?.name}
      description={task?.body}
      bodyMode={task?.body_mode}
      taskListId={task?.task_list_id}
      assigneeId={task?.assignee_id}
      hiddenFromClients={task?.is_hidden_from_clients}
      important={task?.is_important}
      jobTypeId={task?.job_type_id}
      estimate={`${task?.estimate}`}
      subscribers={subscriberIds.length > 0 ? subscriberIds : [loggedUserId]}
      labelIds={task?.labels}
      onClose={onClose}
      hideFromClients={hideFromClients}
      isLoggedUserClient={isLoggedUserClient}
      subtasks={subtasks}
      isBillable={task?.is_billable}
      isTrackingEnabled={project.is_tracking_enabled}
      isProjectNonBillable={project.budget_type === "not_billable"}
    />
  ) : null;
};

RecurringTaskFromTask.propTypes = {
  open: PropTypes.bool,
  taskId: PropTypes.number,
  projectId: PropTypes.number,
  onClose: PropTypes.func,
};

export default RecurringTaskFromTask;
